import { useCallback, useEffect, useMemo, useState } from 'react';
import { ExtractUnits, GenerateInputsBaseFromUnits, RoundOff, MmToCm } from '@themes/common/utils';
import { CalculatePoolQuantity, changeValues } from '../utils';
import { produce } from 'immer';
import { ArrayContains, filterHorn } from '../constants';
export const useProductInputState = (product, extraProducts, quantity, minimumQuantity, id) => {
    const [state, setState] = useState({
        qty: quantity,
        rawQuantity: quantity,
        fields: [],
        areaFieds: [],
        pallets: 0,
        breakingPallets: 0,
        tippings: 0,
        inputs: {},
    });
    const isPool = useMemo(() => !!(product.metafields['SB-P']['Length'] && product.metafields['SB-P']['Width'] && ArrayContains(product.tags, 'Poolsten:Rak')), [product.metafields, product.tags]);
    const isBags = useMemo(() => !!(product.metafields['SB-P']['BagSize'] && product.metafields['SB-P']['BagDensity']), [product.metafields]);
    const isStair = useMemo(() => !!(product.metafields['SB-P']['Length'] && product.metafields['SB-P']['Width'] && ArrayContains(product.tags, 'stair-calculator')), [product.metafields, product.tags]);
    const bagSize = useMemo(() => { var _a; return parseFloat((_a = product.metafields['SB-P']['BagSize']) !== null && _a !== void 0 ? _a : '1'); }, [product.metafields]);
    const bagDensity = useMemo(() => { var _a; return parseFloat((_a = product.metafields['SB-P']['BagDensity']) !== null && _a !== void 0 ? _a : '1'); }, [product.metafields]);
    const extra_product_pool_corner = useMemo(() => (isPool ? extraProducts === null || extraProducts === void 0 ? void 0 : extraProducts.filter(filterHorn)[0] : null), [isPool, extraProducts]);
    const [selectedPoolCorner, setSelectedPoolCorner] = useState((extraProducts !== null && extraProducts !== void 0 ? extraProducts : []).filter(filterHorn).length > 0);
    useEffect(() => {
        var _a, _b, _c, _d;
        let inputs = {};
        let units = [];
        let areaFieds = [];
        if (isPool) {
            const length = MmToCm(parseFloat((_a = product.metafields['SB-P']['Length']) !== null && _a !== void 0 ? _a : '1'));
            const width = MmToCm(parseFloat((_b = product.metafields['SB-P']['Width']) !== null && _b !== void 0 ? _b : '1'));
            units.push(...[
                {
                    factor: 'Width',
                    factorValue: 1,
                    key: 'Width',
                    perQuantity: length,
                    unit: 'Width',
                    unitValue: 'Width',
                    hideButtons: true,
                },
                {
                    factor: 'Length',
                    factorValue: 1,
                    key: 'Length',
                    perQuantity: length,
                    unit: 'Length',
                    unitValue: 'Length',
                    hideButtons: true,
                },
                {
                    factor: 'PCE',
                    factorValue: 1,
                    key: 'PCE',
                    perQuantity: 1,
                    unit: 'PCE',
                    unitValue: 'PCE',
                    hideButtons: false,
                },
            ]);
            inputs = {
                Width: width,
                Length: length,
                PCE: Math.max(quantity, minimumQuantity),
            };
        }
        else if (isBags) {
            areaFieds.push(...[
                {
                    factor: 'Width',
                    factorValue: 1,
                    key: 'Width',
                    perQuantity: 1,
                    unit: 'Width',
                    unitValue: 'Width',
                    hideButtons: true,
                },
                {
                    factor: 'Length',
                    factorValue: 1,
                    key: 'Length',
                    perQuantity: 1,
                    unit: 'Length',
                    unitValue: 'Length',
                    hideButtons: true,
                },
                {
                    factor: 'Thickness',
                    factorValue: 1,
                    key: 'Thickness',
                    perQuantity: 1,
                    unit: 'Thickness',
                    unitValue: 'Thickness',
                    hideButtons: true,
                },
                {
                    factor: 'M3',
                    factorValue: 1,
                    key: 'M3',
                    perQuantity: 1,
                    unit: 'M3',
                    unitValue: 'M3',
                },
                {
                    factor: 'PCE',
                    factorValue: 1,
                    key: 'PCE',
                    perQuantity: 1,
                    unit: 'PCE',
                    unitValue: 'PCE',
                },
            ]);
            const m3 = (quantity * bagSize) / bagDensity;
            inputs = {
                Width: 1,
                Length: 1,
                Thickness: 1,
                M3: RoundOff(m3),
                PCE: quantity,
            };
        }
        else if (isStair) {
            units = ExtractUnits(product.metafields['SB-P']);
            units.push(...[
                {
                    factor: 'Height',
                    factorValue: 1,
                    key: 'Height',
                    perQuantity: 1,
                    unit: 'Height',
                    unitValue: 'Height',
                    hideButtons: true,
                },
                {
                    factor: 'Length',
                    factorValue: 1,
                    key: 'Length',
                    perQuantity: 1,
                    unit: 'Length',
                    unitValue: 'Length',
                    hideButtons: true,
                },
            ]);
            inputs = GenerateInputsBaseFromUnits(units, quantity);
            const height = MmToCm(parseFloat((_c = product.metafields['SB-P']['Thickness']) !== null && _c !== void 0 ? _c : '1'));
            const width = MmToCm(parseFloat((_d = product.metafields['SB-P']['Width']) !== null && _d !== void 0 ? _d : '1'));
            inputs.Length = quantity * (width - 3);
            inputs.Height = quantity * height;
        }
        else {
            units = ExtractUnits(product.metafields['SB-P']);
            inputs = GenerateInputsBaseFromUnits(units, quantity);
        }
        if (inputs['PCE'] !== undefined) {
            inputs['PCE'] = Math.max(quantity, minimumQuantity);
        }
        const indexOfPce = units.findIndex((f) => f.key === 'PCE');
        if (indexOfPce > -1) {
            units[indexOfPce].minimumQuantity = minimumQuantity;
        }
        setState(produce((previousState) => {
            previousState.qty = quantity;
            previousState.rawQuantity = quantity;
            previousState.fields = units;
            previousState.inputs = inputs;
            previousState.areaFieds = areaFieds;
        }));
    }, [id, product, extraProducts, minimumQuantity, isPool, isBags, isStair, bagSize, bagDensity, quantity]);
    const updateValues = useCallback((key, value) => {
        setState((currentState) => changeValues(key, value, currentState, product, extra_product_pool_corner, selectedPoolCorner, bagSize, bagDensity, isPool, isBags, isStair, minimumQuantity));
    }, [product, extra_product_pool_corner, selectedPoolCorner, bagSize, bagDensity, isPool, isBags, isStair, minimumQuantity]);
    const updatePoolCornerSelection = useCallback((selected) => {
        if (extra_product_pool_corner) {
            setState((currentState) => produce(currentState, (previousState) => {
                const newQty = CalculatePoolQuantity(previousState.inputs, product, extra_product_pool_corner, selected);
                previousState.inputs['PCE'] = newQty;
                previousState.qty = Math.ceil(newQty);
                previousState.rawQuantity = newQty;
            }));
        }
    }, [extra_product_pool_corner, product]);
    return {
        state,
        setState,
        isPool,
        isBags,
        isStair,
        bagSize,
        bagDensity,
        extra_product_pool_corner,
        selectedPoolCorner,
        setSelectedPoolCorner,
        updateValues,
        updatePoolCornerSelection,
    };
};
